import {
  CrystalShellConfig,
  CrystalModelType,
  LaserPhysicsConfig,
  PointCloudConfig,
  InnerModelProcessorConfig,
  CausticConfig,
  ShadowConfig,
  SceneOpticsConfig,
} from "@/types/laser-engraving";

// ===================== 激光物理参数常量 =====================

/**
 * 激光内雕物理参数 - 基于真实激光烧蚀工艺
 */
export const LASER_PHYSICS_CONSTANTS: LaserPhysicsConfig = {
  /** 激光烧蚀反射率 (35%) - 基于激光烧蚀反射率研究 */
  laserAblationReflectance: 0.35,

  /** 比尔-朗伯衰减系数 (75%) - 基于光在介质中的衰减规律 */
  depthAttenuationFactor: 0.75,

  /** 分层数量 - 用于体积填充的分层采样 */
  layers: 5,

  /** 表层激光点亮度梯度增强系数 */
  brightnessGradient: 0.1,

  /** 激光能量光谱的物理准确RGB配比 - 基于激光波长特性 */
  spectrumRGB: {
    red: 1.0, // 红光基准
    green: 0.95, // 绿光稍弱
    blue: 0.9, // 蓝光较弱
  },
};

/**
 * 分层缩放参数 - 用于点云分层采样
 */
export const LAYER_SCALING = {
  /** 每层缩放递减率 */
  SCALE_REDUCTION_PER_LAYER: 0.15,

  /** 基础缩放 */
  BASE_SCALE: 1.0,
};

// ===================== 水晶材质默认配置 =====================

/**
 * 水晶材质默认参数 - 基于真实石英水晶物理特性
 */
export const DEFAULT_CRYSTAL_MATERIAL: CrystalShellConfig = {
  /** 透射率 - 完全透明 */
  transmission: 1.0,

  /** 粗糙度 - 抛光水晶表面 */
  roughness: 0.0,

  /** 折射率 - 石英水晶的真实折射率 */
  ior: 1.544,

  /** 色差 - 轻微色散效果 */
  chromaticAberration: 0.005,

  /** 扭曲度 - 无扭曲 */
  distortion: 0.0,

  /** 各向异性 - 轻微增强水晶质感 */
  anisotropy: 0.05,

  /** 颜色 - 纯净水晶色 */
  color: "#ffffff",

  /** 尺寸 - 默认立方体尺寸 */
  size: 1.5,

  /** 厚度 - 默认光学厚度 */
  thickness: 1.5,

  /** 模型类型 - 默认立方体 */
  modelType: "cube" as const,
};

/**
 * 水晶材质控制范围
 */
export const CRYSTAL_MATERIAL_RANGES = {
  transmission: { min: 0, max: 1, step: 0.01 },
  roughness: { min: 0, max: 0.1, step: 0.001 },
  ior: { min: 1.0, max: 2.0, step: 0.001 },
  chromaticAberration: { min: 0, max: 0.05, step: 0.001 },
  distortion: { min: 0, max: 0.5, step: 0.01 },
  anisotropy: { min: 0, max: 1, step: 0.01 },
  size: { min: 0.5, max: 5, step: 0.1 },
  thickness: { min: 0.1, max: 10, step: 0.1 },
};

/**
 * MeshTransmissionMaterial 增强参数
 */
export const TRANSMISSION_MATERIAL_ENHANCEMENTS = {
  /** 渲染分辨率 */
  resolution: 2048,

  /** 采样数 */
  samples: 16,

  /** 时间扭曲 */
  temporalDistortion: 0.0,

  /** 清漆层 */
  clearcoat: 0.1,

  /** 清漆层粗糙度 */
  clearcoatRoughness: 0.0,
};

// ===================== 焦散效果配置默认值 =====================

/**
 * 焦散效果默认配置 - 基于真实光学焦散效果
 * 注意: 光源位置和焦散平面位置现在由 SceneOpticsConfig 统一管理
 */
export const DEFAULT_CAUSTIC_CONFIG: CausticConfig = {
  /** 默认禁用焦散效果 */
  enabled: false,

  /** 不仅仅显示焦散，同时显示物体 */
  causticsOnly: false,

  /** 启用背面渲染以获得完整效果 */
  backside: true,

  /** 焦散颜色 - 淡灰色 */
  color: "#f0f0f0",

  /** 世界半径 - 影响焦散范围 */
  worldRadius: 0.9,

  /** 折射率 - 高折射率产生强烈焦散 */
  ior: 1.8,

  /** 背面折射率 - 较低的背面折射率 */
  backsideIOR: 1.1,

  /** 强度 - 微妙的焦散强度 */
  intensity: 0.1,
};

/**
 * 焦散效果参数控制范围
 * 注意: 光源位置和焦散平面位置的范围现在由 SCENE_OPTICS_RANGES 统一管理
 */
export const CAUSTIC_RANGES = {
  worldRadius: { min: 0.1, max: 3.0, step: 0.1 },
  ior: { min: 1.0, max: 3.0, step: 0.01 },
  backsideIOR: { min: 1.0, max: 2.0, step: 0.01 },
  intensity: { min: 0.0, max: 1.0, step: 0.01 },
};

// ===================== 场景光学效果配置默认值 =====================

/**
 * 场景光学效果默认配置 - Shadow 和 Caustic 系统共享的物理参数
 */
export const DEFAULT_SCENE_OPTICS_CONFIG: SceneOpticsConfig = {
  /** 默认启用场景级光学效果 */
  enabled: true,

  /** 光源位置 - 右上方光源，与原 Caustic 和 Shadow 配置一致 */
  lightSource: [5, 5, -10],

  /** 效果平面位置 - 水晶下方，整体下移0.25 */
  effectPlane: [0, -0.75, 0],
};

/**
 * 场景光学效果参数控制范围
 */
export const SCENE_OPTICS_RANGES = {
  lightSource: { min: -10.0, max: 10.0, step: 0.5 },
  effectPlane: { min: -2.0, max: 2.0, step: 0.1 },
};

// ===================== 点云配置默认值 =====================

/**
 * 点云默认配置
 */
export const DEFAULT_POINT_CLOUD_CONFIG: PointCloudConfig = {
  /** 默认显示点云 */
  showPointCloud: true,

  /** 点的世界尺寸 */
  pointSize: 0.06,

  /** 总点数 */
  pointCount: 10000,

  /** 尺寸倍数 */
  sizeMultiplier: 0.15,

  /** 透明度 */
  opacity: 0.9,
};

/**
 * 点云参数控制范围
 */
export const POINT_CLOUD_RANGES = {
  pointSize: { min: 0.01, max: 0.2, step: 0.001 },
  pointCount: { min: 1000, max: 50000, step: 1000 },
  sizeMultiplier: { min: 0.05, max: 0.5, step: 0.01 },
  opacity: { min: 0.1, max: 1.0, step: 0.01 },
};

// ===================== GLTF模型处理配置 =====================

/**
 * GLTF处理器默认配置
 */
export const DEFAULT_GLTF_CONFIG: InnerModelProcessorConfig = {
  /** 目标尺寸比例（相对于水晶尺寸的70%） */
  targetSizeRatio: 0.7,

  /** 自动居中 */
  autoCenter: true,

  /** X轴旋转角度（-90度，从Y-up转为Z-up） */
  rotationX: -Math.PI / 2,
};

/**
 * 模型文件路径
 */
export const MODEL_PATHS = {
  /** Iron Man 模型文件路径 */
  IRON_MAN: "/assets/models/iron_man.glb",

  MICHAEL: "/assets/models/michael.glb",

  /** 水晶模型文件路径 */
  CRYSTAL: {
    CRYSTAL_1: "/assets/models/crystal/crystal_1.glb",
    CRYSTAL_2: "/assets/models/crystal/crystal_2.glb",
    CRYSTAL_3: "/assets/models/crystal/crystal_3.glb",
    CRYSTAL_4: "/assets/models/crystal/crystal_4.glb",
  },
} as const;

// ===================== 场景配置常量 =====================

/**
 * 相机配置
 */
export const CAMERA_CONFIG = {
  /** 默认位置 */
  position: [-4, 1, 4] as [number, number, number],

  /** 默认FOV */
  fov: 45,

  /** 极角范围 */
  polarAngle: {
    min: 0,
    max: Math.PI / 2,
  },
};

/**
 * 光照系统配置
 */
export const LIGHTING_CONFIG = {
  /** 聚光灯配置 */
  spotLight: {
    position: [5, 5, -10] as [number, number, number],
    angle: 0.15,
    penumbra: 1,
    decay: 0,
  },

  /** 点光源配置 */
  pointLight: {
    position: [-10, -10, -10] as [number, number, number],
    decay: 0,
  },

  /** 环境光强度系数 */
  ambientLightMultiplier: Math.PI,
};

/**
 * 阴影系统默认配置 - 基于真实光照和阴影效果
 * 注意: 光源位置和阴影平面位置现在由 SceneOpticsConfig 统一管理
 */
export const DEFAULT_SHADOW_CONFIG: ShadowConfig = {
  /** 默认启用阴影 */
  enabled: true,

  /** 延迟初始化时间（毫秒） - 分层隔离后可使用较短延迟 */
  initializationDelay: 300,

  /** AccumulativeShadows 配置 */
  accumulative: {
    frames: 50,
    color: "#f0f0f0",
    colorBlend: 1,
    toneMapped: true,
    alphaTest: 0.7,
    opacity: 0.8,
    scale: 12,
  },

  /** RandomizedLight 配置 */
  randomizedLight: {
    amount: 8,
    radius: 10,
    ambient: 0.5,
    bias: 0.001,
  },
};

/**
 * 阴影系统参数控制范围
 * 注意: 光源位置和阴影平面位置的范围现在由 SCENE_OPTICS_RANGES 统一管理
 */
export const SHADOW_RANGES = {
  initializationDelay: { min: 0, max: 2000, step: 100 },
  accumulative: {
    frames: { min: 10, max: 200, step: 10 },
    colorBlend: { min: 0, max: 5, step: 0.1 },
    alphaTest: { min: 0, max: 1, step: 0.01 },
    opacity: { min: 0, max: 1, step: 0.01 },
    scale: { min: 1, max: 50, step: 1 },
  },
  randomizedLight: {
    amount: { min: 1, max: 20, step: 1 },
    radius: { min: 1, max: 50, step: 1 },
    ambient: { min: 0, max: 2, step: 0.01 },
    bias: { min: 0, max: 0.01, step: 0.0001 },
  },
};

/**
 * 环境贴图配置
 */
export const ENVIRONMENT_CONFIG = {
  /** 默认HDR环境贴图URL */
  defaultHDR: "/assets/textures/aerodynamics_workshop_1k.hdr",

  /** 场景背景色 */
  backgroundColor: "#f0f0f0",
};

// ===================== 自动旋转配置 =====================

/**
 * 自动旋转默认配置
 */
export const AUTO_ROTATION_CONFIG = {
  /** 默认启用自动旋转 */
  enabled: true,

  /** 默认旋转速度 */
  speed: 0.5,

  /** 速度范围 */
  speedRange: [0, 2] as [number, number],
};

// ===================== 组件默认位置配置 =====================

/**
 * 激光内雕组件默认配置
 */
export const LASER_ENGRAVING_DEFAULTS = {
  /** 默认位置 */
  position: [0, 0.25, 0] as [number, number, number],

  /** 默认旋转 */
  rotation: [0, Math.PI / 6, 0] as [number, number, number],

  /** 默认缩放 */
  scale: 1 as number,
};

// ===================== 水晶模型工具函数 =====================

/**
 * 根据模型类型获取模型文件路径
 * @param modelType 水晶模型类型
 * @returns 模型文件路径，如果是cube则返回null
 */
export function getCrystalModelPath(
  modelType: CrystalModelType,
): string | null {
  switch (modelType) {
    case "crystal_1":
      return MODEL_PATHS.CRYSTAL.CRYSTAL_1;
    case "crystal_2":
      return MODEL_PATHS.CRYSTAL.CRYSTAL_2;
    case "crystal_3":
      return MODEL_PATHS.CRYSTAL.CRYSTAL_3;
    case "crystal_4":
      return MODEL_PATHS.CRYSTAL.CRYSTAL_4;
    case "cube":
    default:
      return null; // cube使用几何体，不需要模型文件
  }
}

/**
 * 每个模型类型的专用默认配置
 * 基于每种模型的特点进行优化
 */
export const CRYSTAL_MODEL_DEFAULTS: Record<
  CrystalModelType,
  CrystalShellConfig
> = {
  /** 立方体 */
  cube: {
    transmission: 1.0,
    roughness: 0.0,
    ior: 1.544,
    chromaticAberration: 0.005,
    distortion: 0.0,
    anisotropy: 0.05,
    color: "#ffffff",
    size: 1.5,
    thickness: 1.5,
    modelType: "cube",
  },

  /** 水晶模型 1 */
  crystal_1: {
    transmission: 1.0,
    roughness: 0.0,
    ior: 1.03,
    chromaticAberration: 0.005,
    distortion: 0.0,
    anisotropy: 0.05,
    color: "#ffffff",
    size: 1.5,
    thickness: 0.1,
    modelType: "crystal_1",
  },

  /** 水晶模型 2 */
  crystal_2: {
    transmission: 1.0,
    roughness: 0.0,
    ior: 1.03,
    chromaticAberration: 0.005,
    distortion: 0.0,
    anisotropy: 0.05,
    color: "#ffffff",
    size: 1.5,
    thickness: 0.1,
    modelType: "crystal_2",
  },

  /** 水晶模型 3 */
  crystal_3: {
    transmission: 1.0,
    roughness: 0.0,
    ior: 1.03,
    chromaticAberration: 0.005,
    distortion: 0.0,
    anisotropy: 0.05,
    color: "#ffffff",
    size: 1.5,
    thickness: 0.1,
    modelType: "crystal_3",
  },

  /** 水晶模型 4 */
  crystal_4: {
    transmission: 1.0,
    roughness: 0.0,
    ior: 1.03,
    chromaticAberration: 0.005,
    distortion: 0.0,
    anisotropy: 0.05,
    color: "#ffffff",
    size: 1.5,
    thickness: 0.1,
    modelType: "crystal_4",
  },
};

/**
 * 根据模型类型获取对应的默认配置
 * @param modelType 水晶模型类型
 * @returns 该模型类型的默认配置
 */
export function getCrystalModelDefaults(
  modelType: CrystalModelType,
): CrystalShellConfig {
  return { ...CRYSTAL_MODEL_DEFAULTS[modelType] };
}
